import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";

import { GoogleOAuthService } from "@shared/modules/auth/services/GoogleOAuthService";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";

import { useTranslation } from "@shared/utils/internationalization";
import Icon from "@ui-kit/icons/Icon.web";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import useNativeAppCheck from "@shared/hooks/platform/useNativeAppCheck";
import { AuthProfileRepository } from "@shared/modules/auth/AuthProfileRepository";
import { useQueryClient } from "react-query";
import { AnalyticsService } from "@shared/services/analytics";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import type { GoogleLoginButtonSharedProps } from "./GoogleLoginButton.props";
import SocialLoginButton from "../SocialLoginButton/SocialLoginButton.web";

const authProfileRepo = new AuthProfileRepository();

export default function GoogleLoginButton(
  props: GoogleLoginButtonProps
): JSX.Element {
  const { className = "", title, onSuccess } = props;
  const notifier = NotifierFactory.createNotifier();
  const { t } = useTranslation("common");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { isReactNativeApp } = useNativeAppCheck();
  const router = useRouter();
  const { SWITCHES } = useDjangoWaffle();
  const broadcastChannel = useRef(
    new BroadcastChannel(GoogleOAuthService.AUTH_CHANNEL)
  );
  const queryClient = useQueryClient();
  const { setLoggedInStates } = useUserSessionAuthState();

  useEffect(() => {
    // Store the current broadcast channel reference in a variable
    const bc = broadcastChannel.current;

    // Event handler for receiving messages from the broadcast channel
    const handleMessage = async (event: MessageEvent) => {
      if (event.data === GoogleOAuthService.MESSAGES.USER_AUTHENTICATED) {
        // Use a button click as we lack custom interaction types
        AnalyticsService.buttonClick({
          custom_params: {
            datum: "authenticated",
          },
          label: "google_login_button",
        });

        // Normally handled by useUserSessionAuthState but oAuth logins don't work with it
        await queryClient.invalidateQueries(authProfileRepo.repositoryName);
        setLoggedInStates?.();
        onSuccess();
        setIsLoading(false);
      }
    };

    // Add event listener for messages from the broadcast channel
    bc.addEventListener("message", handleMessage);

    // Cleanup function to remove the event listener when the component unmounts
    return () => {
      bc.removeEventListener("message", handleMessage);
    };
  }, [onSuccess]);

  const openGoogleLogin = useCallback(() => {
    AnalyticsService.buttonClick({
      custom_params: {
        datum: "open",
      },
      label: "google_login_button",
    });

    // Build the authentication URL with optional nextUrl parameter
    const nextUrl = router.query.next ? `${router.query.next}` : undefined;
    const authUrl = GoogleOAuthService.buildAuthUrl({ nextUrl });

    if (isReactNativeApp) {
      // For React Native apps, open the Google login in an external browser
      window.location.href = authUrl;
      return;
    }

    // Calculate the position for the popup window
    const width = 600;
    const height = 600;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    // Open a new popup window for Google login
    const authWindow = window.open(
      authUrl,
      "AuthWindow",
      `width=${width}, height=${height}, top=${top}, left=${left}`
    );

    if (!authWindow) {
      setIsLoading(false);
      notifier.error(
        t(
          "Failed to open the authentication window. Please disable your popup blocker and try again."
        )
      );
      return;
    }

    setIsLoading(true);

    const userAuthenticated = false;

    // Interval to check if the popup window is closed
    const checkClosed = setInterval(() => {
      if (authWindow.closed) {
        clearInterval(checkClosed);
        if (!userAuthenticated) {
          setIsLoading(false);
        }
      }
    }, 500);
  }, [router.query.next, onSuccess]);

  // Hide the button if the Google SSO feature flag is not enabled
  if (!SWITCHES?.enable_google_sso) {
    return <></>;
  }

  return (
    <SocialLoginButton
      title={title}
      icon={<Icon.Google boxSize="element.lg" />}
      onClick={openGoogleLogin}
      className={className}
      isLoading={isLoading}
    />
  );
}

export type GoogleLoginButtonProps = GoogleLoginButtonSharedProps & {
  // Add any web specific props here.
};
